import React, { useEffect, useRef } from 'react';
import { ArrowDown, Github, Linkedin, Mail } from 'lucide-react';

const socials = [
  { icon: Github, href: 'https://github.com/dewmith-akalanka', label: 'GitHub' },
  { icon: Linkedin, href: 'https://linkedin.com/in/dewmith-akalanka', label: 'LinkedIn' },
  { icon: Mail, href: 'mailto:akalankadewmith@gmail.com', label: 'Email' },
];

export default function Hero() {
  const lineRef = useRef(null);

  useEffect(() => {
    const el = lineRef.current;
    if (!el) return;
    setTimeout(() => {
      el.style.transition = 'transform 1s cubic-bezier(0.4,0,0.2,1)';
      el.style.transform = 'scaleX(1)';
    }, 800);
  }, []);

  return (
    <section id="hero" style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      padding: 'clamp(100px, 12vw, 140px) clamp(24px, 8vw, 120px) 80px',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Subtle grid background */}
      <div style={{
        position: 'absolute', inset: 0, zIndex: 0,
        backgroundImage: 'linear-gradient(var(--border) 1px, transparent 1px), linear-gradient(90deg, var(--border) 1px, transparent 1px)',
        backgroundSize: '60px 60px',
        opacity: 0.45,
        maskImage: 'radial-gradient(ellipse 90% 70% at 50% 0%, black 20%, transparent 100%)',
      }} />

      {/* Warm blob */}
      <div style={{
        position: 'absolute', top: '5%', right: '-10%',
        width: 'clamp(280px, 45vw, 640px)',
        height: 'clamp(280px, 45vw, 640px)',
        borderRadius: '60% 40% 70% 30% / 50% 60% 40% 50%',
        background: 'radial-gradient(circle, rgba(200,96,42,0.08) 0%, transparent 70%)',
        zIndex: 0, pointerEvents: 'none',
      }} />

      {/* Main content grid */}
      <div className="hero-grid" style={{
        position: 'relative', zIndex: 1,
        maxWidth: 1200, margin: '0 auto', width: '100%',
        display: 'grid',
        gridTemplateColumns: '1fr auto',
        gap: 'clamp(40px, 6vw, 96px)',
        alignItems: 'center',
      }}>

        {/* ── Left: Text ── */}
        <div>
          <p style={{
            fontFamily: 'var(--font-mono)', fontSize: 12, letterSpacing: '0.14em',
            color: 'var(--accent-warm)', textTransform: 'uppercase', marginBottom: 28,
            animation: 'fadeIn 0.6s ease 0.1s both',
          }}>
            ← Software Engineer / Sri Lanka
          </p>

          <h1 style={{
            fontFamily: 'var(--font-display)',
            fontSize: 'clamp(44px, 7vw, 100px)',
            lineHeight: 1.0, letterSpacing: '-0.03em', color: 'var(--ink)',
            marginBottom: 6, animation: 'fadeUp 0.7s ease 0.2s both',
          }}>
            Dewmith
          </h1>
          <h1 style={{
            fontFamily: 'var(--font-display)', fontStyle: 'italic',
            fontSize: 'clamp(44px, 7vw, 100px)', lineHeight: 1.0,
            letterSpacing: '-0.03em', color: 'var(--ink-3)',
            marginBottom: 36, animation: 'fadeUp 0.7s ease 0.3s both',
          }}>
            Akalanka
          </h1>

          <div ref={lineRef} style={{
            height: 1, background: 'var(--border-2)', marginBottom: 32,
            maxWidth: 480, transformOrigin: 'left', transform: 'scaleX(0)',
          }} />

          <p style={{
            fontFamily: 'var(--font-body)', fontSize: 'clamp(15px, 1.6vw, 18px)',
            color: 'var(--ink-2)', maxWidth: 480, lineHeight: 1.75, fontWeight: 300,
            animation: 'fadeUp 0.7s ease 0.5s both',
          }}>
            Building scalable microservices & event-driven systems. 4+ years crafting
            backend infrastructure with <em>Node.js, NestJS, Kafka</em>, and modern frontend stacks.
          </p>

          <div style={{
            marginTop: 44, display: 'flex', flexWrap: 'wrap', gap: 14, alignItems: 'center',
            animation: 'fadeUp 0.7s ease 0.65s both',
          }}>
            <a href="#projects" className="btn-primary" style={{
              fontFamily: 'var(--font-mono)', fontSize: 12, letterSpacing: '0.08em',
              textTransform: 'uppercase', background: 'var(--ink)', color: 'var(--cream)',
              padding: '13px 28px', borderRadius: 2, border: '1px solid var(--ink)', transition: 'all 0.25s',
              display: 'inline-block',
            }}
              onMouseEnter={e => { e.target.style.background = 'var(--accent-warm)'; e.target.style.borderColor = 'var(--accent-warm)'; }}
              onMouseLeave={e => { e.target.style.background = 'var(--ink)'; e.target.style.borderColor = 'var(--ink)'; }}>
              View Projects
            </a>
            <a href="#contact" style={{
              fontFamily: 'var(--font-mono)', fontSize: 12, letterSpacing: '0.08em',
              textTransform: 'uppercase', color: 'var(--ink)', padding: '13px 28px',
              borderRadius: 2, border: '1px solid var(--border-2)', transition: 'all 0.25s',
              display: 'inline-block',
            }}
              onMouseEnter={e => { e.target.style.borderColor = 'var(--ink)'; }}
              onMouseLeave={e => { e.target.style.borderColor = 'var(--border-2)'; }}>
              Get in Touch
            </a>
          </div>

          <div style={{ marginTop: 48, display: 'flex', gap: 22, animation: 'fadeIn 0.7s ease 0.85s both' }}>
            {socials.map(({ icon: Icon, href, label }) => (
              <a key={label} href={href} target="_blank" rel="noreferrer" aria-label={label}
                style={{ color: 'var(--ink-3)', transition: 'color 0.2s' }}
                onMouseEnter={e => { e.currentTarget.style.color = 'var(--accent-warm)'; }}
                onMouseLeave={e => { e.currentTarget.style.color = 'var(--ink-3)'; }}>
                <Icon size={20} strokeWidth={1.5} />
              </a>
            ))}
          </div>
        </div>

        {/* ── Right: Photo ── */}
        <div className="hero-photo-wrap" style={{
          position: 'relative',
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'flex-end',
          alignSelf: 'stretch',
          animation: 'fadeIn 0.9s ease 0.4s both',
          overflow: 'visible',
        }}>
          {/* Dot grid accent top-left */}
          <div style={{
            position: 'absolute', top: 40, left: -8,
            width: 80, height: 80, zIndex: 0,
            backgroundImage: 'radial-gradient(circle, var(--border-2) 1.5px, transparent 1.5px)',
            backgroundSize: '10px 10px',
            opacity: 0.8,
            pointerEvents: 'none',
          }} />

          {/* Photo — raw PNG, no box, bleeds to bottom */}
          <img
            src="/me.png"
            alt="Dewmith Akalanka"
            className="hero-photo-img"
            style={{
              position: 'relative', zIndex: 1,
              width: 'clamp(320px, 38vw, 520px)',
              height: 'auto',
              objectFit: 'contain',
              objectPosition: 'bottom center',
              display: 'block',
              filter: 'drop-shadow(0 20px 48px rgba(0,0,0,0.10))',
              marginBottom: '-80px', /* bleed into section padding */
            }}
          />

          {/* Available badge */}
          <div className="hero-badge-top" style={{
            position: 'absolute', top: 48, right: 0,
            background: 'var(--white)', border: '1px solid var(--border)',
            borderRadius: 4, padding: '9px 14px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.07)',
            animation: 'fadeUp 0.6s ease 1s both',
            whiteSpace: 'nowrap', zIndex: 2,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#22c55e', flexShrink: 0 }} />
              <p style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: '#16a34a', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Available</p>
            </div>
            <p style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--ink-2)' }}>Open to opportunities</p>
          </div>

          {/* Stack badge */}
          <div className="hero-badge-bottom" style={{
            position: 'absolute', bottom: 100, left: 0,
            background: 'var(--ink)', borderRadius: 4, padding: '9px 14px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.18)',
            animation: 'fadeUp 0.6s ease 1.1s both',
            whiteSpace: 'nowrap', zIndex: 2,
          }}>
            <p style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'rgba(255,255,255,0.35)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 4 }}>Primary Stack</p>
            <p style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--cream)' }}>NestJS · Kafka · React</p>
          </div>
        </div>

      </div>

      {/* Scroll indicator */}
      <div style={{
        position: 'absolute', bottom: 32, left: '50%', transform: 'translateX(-50%)',
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
        color: 'var(--ink-4)', animation: 'fadeIn 1s ease 1.3s both',
        zIndex: 1,
      }}>
        <p style={{ fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase' }}>Scroll</p>
        <ArrowDown size={14} strokeWidth={1.5} style={{ animation: 'bounce 2s ease infinite' }} />
      </div>

      <style>{`
        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(6px); }
        }

        /* ── Mobile: photo above text, centered ── */
        @media (max-width: 680px) {
          .hero-grid {
            grid-template-columns: 1fr !important;
          }
          .hero-photo-wrap {
            order: -1;
            justify-content: center !important;
            margin-bottom: -32px;
          }
          .hero-photo-img {
            width: clamp(200px, 62vw, 300px) !important;
            margin-bottom: 0 !important;
          }
          .hero-badge-top,
          .hero-badge-bottom {
            display: none !important;
          }
        }

        @media (max-width: 400px) {
          .hero-grid { gap: 20px !important; }
        }
      `}</style>
    </section>
  );
}
